import axios from "axios";
import { ArrowLeft, SquarePen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useCurrentUser } from "base/app";
import { GenericException } from "base/ui/errors";
import { Popup } from "base/ui/popups";
import { LoadingOverlay, EmptyView } from "base/ui/status";
import { useRerender } from "base/utils/common";

import { SearchView } from "../../components/ui/SearchView";
import { ORG_FEATURE_FLAGS } from "../../constants";

const FEATURE_FLAGS_LIST = [
  {
    id: ORG_FEATURE_FLAGS.LOYALTY_QR,
    name: "Loyalty QR",
    type: "Platform Feature",
    default_config: { is_active: true, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.DOWNLOAD_ORDER_INVOICE,
    name: "Download Order Invoice",
    type: "Platform Feature",
    default_config: { is_active: true, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.ROUTE_PLAN,
    name: "Route Plan",
    type: "Platform Feature",
    default_config: { is_active: false, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.ROUTE_PLAN_MANUAL_CHECKIN_CHECKOUT,
    name: "Route Plan - Manual CheckIn / CheckOut",
    type: "Platform Feature",
    default_config: { is_active: false, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.ROUTE_PLAN_SHOP_PHOTO,
    name: "Route Plan - Take Shop Photo",
    type: "Platform Feature",
    default_config: { is_active: false, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.NON_COMMERCE,
    name: "Non Commerce Organization",
    type: "Platform Setting",
    default_config: { is_active: false, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.SHOW_PAYMENT_LINK_AS_BUTTON,
    name: "Show Payment Link As Button",
    type: "Platform Setting",
    default_config: { is_active: false, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.GEO_BASED_SHOP_SELECTION,
    name: "Geo Based Shop Selection",
    type: "Platform Setting",
    default_config: { is_active: false, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.CONVERSATIONAL_ANALYZER,
    name: "Conversation Analyzer",
    type: "Platform Feature",
    default_config: { is_active: false, is_visible_on_inactive: false }
  },
  {
    id: ORG_FEATURE_FLAGS.RETENTION_AGENT,
    name: "AI Retention Agent",
    type: "Platform Feature",
    default_config: { is_active: false, is_visible_on_inactive: false }
  }
];

const UpgradePlan = ({ org_id, billing_plan: _billing_plan, onComplete }) => {
  const plans = {
    STARTER: "STARTER",
    TRIAL_7_DAYS: "TRIAL_7_DAYS",
    ENTERPRISE: "ENTERPRISE"
  };
  const [billing_plan, setBillingPlan] = useState();

  const ctx = useRef({
		selected_plan: null,
		selected_plan_duration: null,
		disable_dashboard: false,
		disabled_reason: "",
	}).current;
  const rerender = useRerender();

  const updatePlan = () => {
    if (!ctx.selected_plan_duration) {
      return
    }
    if (ctx.is_loading) return;
    ctx.is_loading = true;
    rerender();
    axios
      .post(`/api/admin/dorg/${org_id}?action=update_billing_plan`, {
        billing_plan: {
          _type: ctx.selected_plan || billing_plan?._type,
          expires_at: ctx.selected_plan_duration,
          disable_dashboard_access: ctx.disable_dashboard,
          disabled_reason: ctx.disable_dashboard ? (ctx.disabled_reason?.trim() || "You have open invoices due for more than 30 days. Please settle the outstanding balance to continue using Flowcart") : null
        }
      })
      .then((resp) => {
        if (resp.data.errors) {
          Popup.show("Errors", <GenericException ex={resp.data.errors} />);
          return;
        }
        onComplete && onComplete(resp.data.success)
      })
      .finally(() => {
        ctx.is_loading = false;
        rerender();
      });
  }

  useEffect(() => {
    if(_billing_plan) {
      ctx.selected_plan = _billing_plan._type
      ctx.selected_plan_duration = _billing_plan.expires_at
      ctx.disable_dashboard = _billing_plan.disable_dashboard_access
      ctx.disabled_reason = _billing_plan?.disabled_reason
      setBillingPlan(_billing_plan)
    }
  }, [_billing_plan])


  return (
		<div className="tw-p-4">
			{ctx.is_loading || !billing_plan ? <LoadingOverlay /> : null}
      {billing_plan && 
        <>
          <div className="tw-flex tw-flex-col tw-mb-4 tw-gap-1 text-sm">
            <div className="tw-font-medium">Select Plan</div>
            <select
              className="twx-dropdown tw-flex-1"
              defaultValue={billing_plan?._type}
              onChange={(evt) => {
                ctx.selected_plan = evt.target.value;
              }}
            >
              <option value="" disabled={true}>
                Select Plan
              </option>
              {Object.keys(plans).map((plan) => (
                <option key={plan} value={plan}>
                  {plans[plan]}
                </option>
              ))}
            </select>
          </div>
          <div className="tw-flex tw-flex-col tw-mb-4 tw-gap-1 text-sm">
            <div className="tw-font-medium">Plan Duration</div>
            <input
              type="date"
              className="input-primary tw-flex-1"
              min={new Date().toISOString().split("T")[0]}
              defaultValue={billing_plan?.expires_at ? new Date(billing_plan?.expires_at).toISOString().split("T")[0] : ""}
              onChange={(e) => {
                const selected_date = new Date(e.target.value).getTime();
                ctx.selected_plan_duration = selected_date;
              }}
            />
          </div>
          <div className="tw-flex tw-items-center tw-mb-4 tw-gap-1 text-sm">
            <input
              type="checkbox"
              className="input-primary"
              defaultChecked={billing_plan?.disable_dashboard_access}
              onChange={(e) => {
                ctx.disable_dashboard = e.target.checked;
                rerender();
              }}
            />
            <div className="tw-font-medium">Disable Dashboard Access</div>
          </div>
          {ctx.disable_dashboard &&
              <div className="tw-flex tw-flex-col tw-mb-4 tw-gap-1 text-sm">
                <div className="tw-font-medium">Disabled Reason</div>
                <textarea
                  type="text"
                  className="input-primary tw-flex-1"
                  defaultValue={billing_plan?.disabled_reason || "You have open invoices due for more than 30 days. Please settle the outstanding balance to continue using Flowcart"}
                  onChange={(e) => {
                    ctx.disabled_reason = e.target.value;
                  }}
                  rows={3}
                />
              </div>
          }
        </>
      }
			<div className="tw-flex tw-gap-2 tw-mt-4 tw-w-full tw-justify-end">
				<button
					type="button"
					className={`btn-secondary ${ctx.is_loading ? "tw-opacity-70" : ""}`}
					disabled={ctx.is_loading}
					onClick={() => onComplete()}
				>
					Cancel
				</button>
				<button
					type="button"
					className={`btn-primary-v2 ${ctx.is_loading || !billing_plan?.expires_at ? "tw-opacity-70" : ""}`}
					disabled={ctx.is_loading || !billing_plan?.expires_at}
					onClick={updatePlan}
				>
					Save
				</button>
			</div>
		</div>
	);
}

const OrgFeatureFlags = () => {
  const params = useParams();
  const user = useCurrentUser();
  const { delivery_org_id } = params || {};
  const [feature_flags, setFeatureFlags] = useState();
  const [billing_plan, setBillingPlan] = useState();
  const [is_loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [updated_feature_flags, setUpdatedFeatureFlags] = useState({});
  const [filtered_flags, setFilteredFlags] = useState(FEATURE_FLAGS_LIST);
  const [filters, setFilters] = useState({
    is_visible_on_inactive: "",
    is_active: "",
    search_text: "",
  });
  const can_modify = user?.roles?.superadmin || user?.roles?.staff;
  const is_save_disabled = Object.keys(updated_feature_flags).length === 0;

  const fetchDeliveryOrg = () => {
    if (is_loading) return;
    setLoading(true);
    axios
      .get(`/api/staff/delivery_org/${delivery_org_id}`)
      .then((resp) => {
        if (resp.data.errors) {
          Popup.show("Errors", <GenericException ex={resp.data.errors} />);
          return;
        }
        setFeatureFlags(resp.data.delivery_org?.feature_flags);
        setBillingPlan(resp.data.delivery_org?.billing_plan);
      })
      .catch((err) => {
        Popup.show("Errors", <GenericException ex={err} />);
      })
      .finally(() => setLoading(false));
  };

  const upgradePlan = () => {
    const popup = Popup.show("Upgrade Plan", <UpgradePlan org_id={delivery_org_id} billing_plan={billing_plan} onComplete={(new_plan) => {
      popup.close();
      new_plan && fetchDeliveryOrg();
    }} />);
  }

  useEffect(() => {
    if (delivery_org_id) {
      fetchDeliveryOrg();
    }
  }, [delivery_org_id]);

  const updateField = (id, key) => {
    return (evt) => {
      const feature_flag = updated_feature_flags[id] ||
        feature_flags[id] || { is_visible_on_inactive: false, is_active: true };
      const _feature_flag = { ...feature_flag };
      _feature_flag[key] =
        evt.target.value === "true"
          ? true
          : evt.target.value === "false"
            ? false
            : evt.target.value;
      setUpdatedFeatureFlags({ ...updated_feature_flags, [id]: _feature_flag });
    };
  };

  const save = () => {
    if (is_loading) return;
    setLoading(true);
    axios
      .post(
        `/api/staff/delivery_org/${delivery_org_id}?action=update_feature_flags`,
        {
          feature_flags: updated_feature_flags,
        }
      )
      .then((resp) => {
        if (resp.data.errors) {
          Popup.show("Errors", <GenericException ex={resp.data.errors} />);
          return;
        }
        setFeatureFlags(resp.data.delivery_org?.feature_flags);
        setUpdatedFeatureFlags({});
      })
      .catch((err) => {
        Popup.show("Errors", <GenericException ex={err} />);
      })
      .finally(() => setLoading(false));
  };

  const reset = () => {
    setUpdatedFeatureFlags({});
  };

  useEffect(() => {
    const search_text = filters.search_text?.toLowerCase();
    setFilteredFlags(
      FEATURE_FLAGS_LIST.filter((flag) => {
        const config =
          updated_feature_flags[flag.id] || feature_flags?.[flag.id] || {};
        const is_active = config.is_active || false;
        const is_visible_on_inactive = config.is_visible_on_inactive || false;
        return (
          (!search_text || flag.name.toLowerCase().includes(search_text)) &&
          (!filters.is_active || is_active?.toString() === filters.is_active) &&
          (!filters.is_visible_on_inactive ||
            is_visible_on_inactive?.toString() ===
            filters.is_visible_on_inactive)
        );
      })
    );
  }, [filters]);

  if (!user?.roles?.staff && !user?.roles?.superadmin) {
    return <EmptyView
        title="You donot have permission to view this."
        height="500px" 
    />
  }

  return (
    <div className="tw-bg-gray-bg">
      <div className="tw-w-full lg:tw-w-[90%] lg:tw-ml-[5%] tw-bg-white lg:home-minh">
        <div className="tw-p-4">
          <div className="lg:tw-flex tw-items-center tw-justify-between tw-gap-6">
            <div className="tw-flex">
              <ArrowLeft
                size={20}
                className="tw-mr-4 tw-mt-1 tw-cursor-pointer"
                onClick={() =>
                  navigate(
                    `${window.location.pathname.replace(`/${delivery_org_id}`, "")}`,
                    { replace: true }
                  )
                }
              />
              <div>
                <div className="tw-flex tw-items-center tw-gap-4">
                  <span className="tw-font-bold tw-capitalize">
                    {delivery_org_id} Permissions
                  </span>
                  {
                    billing_plan
                      ? <span className="tw-bg-green-200 tw-text-green-900 tw-font-bold tw-text-xs tw-rounded-sm tw-px-2 tw-py-1 tw-flex tw-items-center">
                        {billing_plan._type || billing_plan.type}
                        <SquarePen size={13} className="tw-ml-2 tw-cursor-pointer" onClick={upgradePlan} />
                      </span>
                      : null
                  }

                </div>
                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                  {can_modify ? "Enable, Disable, Hide or unhide" : "View"}{" "}
                  permissions from the following table
                </p>
              </div>
            </div>
            <div className="md:tw-flex tw-gap-4 max-lg:tw-mt-4 tw-items-center lg:tw-justify-between">
              <SearchView
                placeholder="Search Permissions"
                className="tw-grow"
                onSearch={(search_text) =>
                  setFilters({ ...filters, search_text })
                }
              />
              <div className="max-md:tw-mt-4 tw-flex tw-items-center tw-gap-4">
                <select
                  className={`tw-text-xs tw-text-gray-600 tw-border tw-rounded tw-px-4 tw-py-2 tw-outline-0 ${filters.is_visible_on_inactive ? "tw-border-secondary-lighter" : ""}`}
                  onChange={(evt) =>
                    setFilters({
                      ...filters,
                      is_visible_on_inactive: evt.target.value,
                    })
                  }
                >
                  <option value="">Visibility</option>
                  <option value="false">Hidden</option>
                  <option value="true">Visible &#128274;</option>
                </select>
                <select
                  className={`tw-text-xs tw-text-gray-600 tw-border tw-rounded tw-px-4 tw-py-2 tw-outline-0 ${filters.is_active ? "tw-border-secondary-lighter" : ""}`}
                  onChange={(evt) =>
                    setFilters({ ...filters, is_active: evt.target.value })
                  }
                >
                  <option value="">Status</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <table className="tw-table-auto tw-w-full tw-mb-8">
          <thead className="tw-bg-gray-bg tw-text-gray-600 tw-text-left">
            <tr className="tw-border-y">
              <th className="tw-px-4 tw-py-2 tw-text-xs">Permission</th>
              <th className="tw-px-4 tw-py-2 tw-text-xs">Type</th>
              <th className="tw-px-4 tw-py-2 tw-text-xs">Value</th>
              <th className="tw-px-4 tw-py-2 tw-text-xs">Visibility</th>
              <th className="tw-px-4 tw-py-2 tw-text-xs">Status</th>
            </tr>
          </thead>
          <tbody className="tw-text-xs">
            {filtered_flags.map(({ id, name, type }) => {
              const config = updated_feature_flags[id] ||
                feature_flags?.[id] ||
                FEATURE_FLAGS_LIST.find(f => f.id === id)?.default_config || {
                is_visible_on_inactive: false,
                is_active: true,
              };
              const is_active = config.is_active ? "true" : "false";
              const is_visible_on_inactive = config.is_visible_on_inactive
                ? "true"
                : "false";

              return (
                <tr key={id + JSON.stringify(config)} className="tw-border-y">
                  <td className="tw-px-4 tw-py-2">{name}</td>
                  <td className="tw-px-4 tw-py-2">{type}</td>
                  <td className="tw-px-4 tw-py-2">
                    {feature_flags?.[id]?.value || "-"}
                  </td>
                  <td
                    className={`tw-px-4 tw-py-2 ${is_visible_on_inactive === "true" ? "tw-text-secondary-lighter" : "tw-text-black"} tw-outline-0`}
                  >
                    {is_active === "true"
                      ? "-"
                      : can_modify
                        ?
                        <select
                          value={is_visible_on_inactive}
                          onChange={updateField(id, "is_visible_on_inactive")}
                        >
                          <option value="true">Visible &#128274;</option>
                          <option value="false">Hidden</option>
                        </select>
                        : is_visible_on_inactive === "true"
                          ? "Visible"
                          : "Hidden"
                    }
                  </td>
                  <td
                    className={`tw-px-4 tw-py-2 ${is_active === "true" ? "tw-text-secondary-lighter" : "tw-text-black"} tw-outline-0`}
                  >
                    {can_modify
                      ? <select
                        value={is_active}
                        onChange={updateField(id, "is_active")}
                        className={`${is_active === "true" ? "tw-text-green-600" : "tw-text-red-600"} tw-outline-0`}
                      >
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                      : is_active === "true"
                        ? "Enabled"
                        : "Disabled"
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {can_modify
          ? (
            <div className="tw-px-4 tw-py-2 max-sm:tw-absolute tw-bottom-0 tw-w-full tw-flex tw-gap-4">
              <button
                type="button"
                className={`tw-text-sm tw-rounded tw-text-white tw-border tw-border-secondary-lighter tw-bg-secondary-lighter tw-px-4 tw-py-2 max-sm:tw-flex-1 ${is_save_disabled ? "tw-opacity-70" : ""}`}
                disabled={is_save_disabled}
                onClick={save}
              >
                Save
              </button>
              <button
                type="button"
                className={`tw-text-sm tw-rounded tw-text-gray-600 tw-border tw-px-4 tw-py-2 max-sm:tw-flex-1 ${is_save_disabled ? "tw-opacity-70" : ""}`}
                disabled={is_save_disabled}
                onClick={reset}
              >
                Reset
              </button>
            </div>
          )
          : null}
      </div>
    </div>
  );
};

function isFeatureFlagEnabled(org, feature_flag) {
  if (!org || !org.feature_flags || !org.feature_flags[feature_flag]) {
    return FEATURE_FLAGS_LIST.find((flag) => flag.id === feature_flag)?.default_config?.is_active;
  }
  return org.feature_flags[feature_flag]?.is_active
}

export { FEATURE_FLAGS_LIST, OrgFeatureFlags, isFeatureFlagEnabled };
